<template>
    <DrawerRoot v-model:open="visible">
        <DrawerTrigger class="flex items-center justify-center gap-2 bg-red-600 p-3 px-4 font-bold">
            {{ modelValue }}

            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12.0001 13.5856L6.70718 8.29274L5.29297 9.70696L12.0001 16.4141L18.7072 9.70695L17.293 8.29274L12.0001 13.5856Z"
                    fill="white"
                />
            </svg>
        </DrawerTrigger>

        <DrawerPortal>
            <DrawerOverlay class="fixed inset-0 z-[100] bg-black/50" />
            <DrawerContent
                aria-describedby="undefined"
                class="bg-bg border-stroke-1 fixed inset-x-0 bottom-0 z-[101] flex flex-col border-t-1"
            >
                <DrawerTitle class="sr-only">{{ $t('general.number_input') }}</DrawerTitle>
                <DrawerHandle class="mx-auto mt-2 mb-2 h-1 w-10 rounded-full bg-zinc-600" />
                <div class="flex w-full gap-4 p-4">
                    <div
                        class="bg-card border-stroke-1 flex-1 border-1 p-2 px-3 text-lg text-[#797979]"
                    >
                        {{ displayValue }}
                    </div>
                    <diagonal-cut-button
                        class="flex-0"
                        variant="primary"
                        cut-position="left-top"
                        @click="confirm"
                        >{{ $t('general.confirm') }}</diagonal-cut-button
                    >
                </div>

                <div class="border-stroke-1 h-1 w-full border-t-1"></div>

                <div class="flex w-full gap-2 p-4">
                    <diagonal-cut-button
                        v-for="(preset, index) in presets"
                        :key="preset.value"
                        class="flex-1 [&>*]:text-lg"
                        :class="{
                            'clip-both-corners':
                                (index !== 0 && index !== presets.length - 1) ||
                                displayValue == preset.value,
                        }"
                        :cut-position="
                            index === 0
                                ? 'right-top'
                                : index === presets.length - 1
                                  ? 'left-top'
                                  : null
                        "
                        :variant="displayValue == preset.value ? 'primary' : 'secondary'"
                        @click="setPreset(preset.value)"
                        borderColor="bg-[#2B2B2B]"
                        fillColor="bg-[#232323]"
                        >{{ preset.label }}</diagonal-cut-button
                    >
                </div>

                <div class="bg-card grid grid-cols-6 gap-[2px] p-[2px]">
                    <button
                        v-for="value in 9"
                        :key="value"
                        class="bg-bg col-span-2 flex w-full items-center justify-center rounded p-5 text-2xl font-bold"
                        :class="disabledDigits.includes(value) ? 'opacity-50' : ''"
                        @click="appendDigit(value)"
                    >
                        {{ value }}
                    </button>
                    <button
                        class="bg-bg col-span-3 flex w-full items-center justify-center rounded p-5 text-2xl font-bold"
                        @click="appendDigit(0)"
                        :class="disabledDigits.includes(0) ? 'opacity-50' : ''"
                    >
                        0
                    </button>
                    <button
                        class="bg-bg col-span-3 flex w-full items-center justify-center rounded p-5 text-2xl font-bold"
                        @click="backspace"
                    >
                        <svg
                            width="33"
                            height="32"
                            viewBox="0 0 33 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M12.6588 8.37402L14.5098 10.2936L11.5546 13.1433L25.5843 13.143V21.3335H22.9176V15.8098L11.5544 15.8099L14.5098 18.6597L12.6588 20.5793L6.33008 14.4767L12.6588 8.37402Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </div>
                <div class="pb-safe"></div>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>
</template>

<script>
import DiagonalCutButton from '../DiagonalCutButton.vue'
import {
    DrawerRoot,
    DrawerTrigger,
    DrawerPortal,
    DrawerOverlay,
    DrawerContent,
    DrawerHandle,
    DrawerTitle,
} from 'vaul-vue'

export default {
    name: 'IntegerKeyboard',
    components: {
        DiagonalCutButton,
        DrawerRoot,
        DrawerTrigger,
        DrawerPortal,
        DrawerOverlay,
        DrawerContent,
        DrawerHandle,
        DrawerTitle,
    },
    props: {
        modelValue: {
            default: 0,
        },
        min: {
            type: Number,
            required: false,
            default: 0,
        },
        max: {
            type: Number,
            required: false,
            default: 9999,
        },
        presets: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            visible: false,
            tempValue: String(this.modelValue ?? ''),
            isPristine: true,
        }
    },
    computed: {
        displayValue() {
            return this.tempValue === '' || this.tempValue === '0'
                ? this.$t('general.choose_score')
                : this.tempValue
        },
        disabledDigits() {
            // Returns an array of digits (0-9) that would exceed max if appended
            const disabled = []
            for (let d = 0; d <= 9; d++) {
                let newValue = this.tempValue === '0' ? String(d) : this.tempValue + String(d)
                let num = Number(newValue)
                if (num > this.max || num < this.min) {
                    disabled.push(d)
                }
            }
            return disabled
        },
    },
    watch: {
        modelValue(newVal) {
            if (!this.visible) {
                this.tempValue = String(newVal ?? '')
            }
        },
        visible(newVal) {
            if (newVal) {
                this.isPristine = true
                this.tempValue = String(this.modelValue ?? '')
            }
        },
    },
    methods: {
        open() {
            this.tempValue = String(this.modelValue ?? '')
            this.visible = true
        },
        appendDigit(digit) {
            if (this.isPristine) {
                this.tempValue = ''
                this.isPristine = false
            }

            let newValue = this.tempValue === '0' ? String(digit) : this.tempValue + String(digit)
            if (Number(newValue) > this.max) return
            this.tempValue = newValue
        },
        backspace() {
            this.isPristine = false
            this.tempValue = this.tempValue.slice(0, -1) || '0'
        },
        setPreset(val) {
            this.isPristine = false
            this.tempValue = String(val)
        },
        confirm() {
            this.visible = false
            this.$emit('update:modelValue', Number(this.tempValue))
        },
    },
}
</script>
